import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../api';
import { showError } from '../utils/toast';
import { ButtonSpinner } from './common/LoadingSpinner';
import {
  isBiometricAvailable,
  getBiometricType,
  authenticateWithBiometrics,
  saveCredentials,
  getSavedEmail,
  isBiometricEnabled
} from '../utils/biometricAuth';

// Validation schema
const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required')
});

const Login = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState(null);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [enableBiometric, setEnableBiometric] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  // Check biometric availability on mount
  useEffect(() => {
    const checkBiometric = async () => {
      if (!window.Capacitor) return;

      const available = await isBiometricAvailable();
      setBiometricAvailable(available);

      if (available) {
        const type = await getBiometricType();
        setBiometricType(type);

        const enabled = await isBiometricEnabled();
        setBiometricEnabled(enabled);

        // Pre-fill email if biometric is enabled
        if (enabled) {
          const savedEmail = await getSavedEmail();
          if (savedEmail) {
            setValue('email', savedEmail);
          }
        }
      }
    };

    checkBiometric();
  }, [setValue]);

  // Handle biometric login
  const handleBiometricLogin = async () => {
    setIsLoading(true);
    try {
      const result = await authenticateWithBiometrics();

      if (result.success) {
        // Use the retrieved credentials to log in
        const response = await api.post('/auth/login', {
          email: result.email,
          password: result.password
        });

        // Store token for mobile
        if (window.Capacitor && response.data.token) {
          localStorage.setItem('authToken', response.data.token);
          console.log('📱 Auth token stored for mobile app');

          // Fetch CSRF token
          try {
            const csrfResponse = await api.get('/auth/csrf');
            if (csrfResponse.data?.csrfToken) {
              localStorage.setItem('csrfToken', csrfResponse.data.csrfToken);
              console.log('📱 CSRF token fetched and stored after login');
            }
          } catch (csrfError) {
            console.warn('⚠️ Could not fetch CSRF token:', csrfError);
          }
        }

        // Store full user data for mobile
        if (window.Capacitor && response.data.user) {
          localStorage.setItem('userData', JSON.stringify(response.data.user));
        }

        onLogin(response.data.user);
        navigate('/');
      } else {
        showError(result.error || 'Biometric authentication failed');
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      showError(error.response?.data?.error || 'Biometric login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await api.post('/auth/login', data);
      
      // For mobile apps, store token in localStorage
      if (window.Capacitor && response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        console.log('📱 Auth token stored for mobile app');
        
        // Fetch CSRF token for mobile apps (production server requires it)
        // Store in localStorage because cookies don't work cross-origin
        try {
          const csrfResponse = await api.get('/auth/csrf');
          if (csrfResponse.data?.csrfToken) {
            localStorage.setItem('csrfToken', csrfResponse.data.csrfToken);
            console.log('📱 CSRF token fetched and stored after login');
          }
        } catch (csrfError) {
          console.warn('⚠️ Could not fetch CSRF token:', csrfError);
        }

        // Save credentials for biometric auth if checkbox is checked
        if (enableBiometric && biometricAvailable) {
          await saveCredentials(data.email, data.password);
          console.log('📱 Credentials saved for biometric auth');
        }
      }
      
      // Store full user data for mobile before calling onLogin
      if (window.Capacitor && response.data.user) {
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        console.log('📱 Full user data stored during login:', response.data.user);
      }
      
      // Remember biometric preference for startup gate (mobile only)
      if (window.Capacitor) {
        if (enableBiometric) {
          localStorage.setItem('biometricEnabled', 'true');
        } else {
          localStorage.removeItem('biometricEnabled');
        }
      }

      onLogin(response.data.user);
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      showError(error.response?.data?.error || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-blue-500/5 to-transparent rounded-full"></div>
      </div>

      {/* Grid Pattern Overlay (subtle) */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]"></div>

      {/* Login Container */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          {/* Main Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20">
            {/* Logo and Title Section */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 mb-6 shadow-lg">
                <img 
                  src="/mibot-logo.png" 
                  alt="MIBOT Logo" 
                  className="w-12 h-12 object-contain"
                />
              </div>
              
              <h1 className="text-3xl font-bold text-white mb-2">
                Welcome Back
              </h1>
              <p className="text-blue-200 text-sm">
                Sign in to MIBOT Onboarding
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Email Field */}
              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-semibold text-white mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  {...register('email')}
                  className={`
                    w-full px-4 py-3 rounded-xl
                    bg-white/10 backdrop-blur-sm
                    border ${errors.email ? 'border-red-500' : 'border-white/20'}
                    text-white placeholder-white/50
                    focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50
                    transition-all duration-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                  placeholder="Enter your email address"
                  disabled={isLoading}
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label 
                  htmlFor="password" 
                  className="block text-sm font-semibold text-white mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  {...register('password')}
                  className={`
                    w-full px-4 py-3 rounded-xl
                    bg-white/10 backdrop-blur-sm
                    border ${errors.password ? 'border-red-500' : 'border-white/20'}
                    text-white placeholder-white/50
                    focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50
                    transition-all duration-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Enable Biometric Checkbox (only show if biometric available and not already enabled) */}
              {window.Capacitor && biometricAvailable && !biometricEnabled && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enableBiometric"
                    checked={enableBiometric}
                    onChange={(e) => setEnableBiometric(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-600 focus:ring-2 focus:ring-blue-500/50"
                  />
                  <label htmlFor="enableBiometric" className="ml-2 text-sm text-white/90">
                    Enable {biometricType === 'faceId' ? 'Face ID' : biometricType === 'touchId' ? 'Touch ID' : 'Biometric'} login
                  </label>
                </div>
              )}

              {/* Login Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`
                  relative w-full py-3.5 px-6 rounded-xl font-semibold text-white
                  bg-gradient-to-r from-blue-600 to-purple-600
                  hover:from-blue-700 hover:to-purple-700
                  focus:outline-none focus:ring-4 focus:ring-blue-500/50
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all duration-200
                  flex items-center justify-center gap-2
                  shadow-lg shadow-blue-500/25
                `}
              >
                {isLoading ? (
                  <>
                    <ButtonSpinner />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    <span>Sign In</span>
                  </>
                )}
              </button>

            </form>

            {/* Biometric Login Button (only show if biometric enabled) */}
            {window.Capacitor && biometricEnabled && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white/10 text-white/70">Or</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleBiometricLogin}
                  disabled={isLoading}
                  className={`
                    w-full py-3.5 px-6 rounded-xl font-semibold text-white
                    bg-white/10 backdrop-blur-sm border border-white/20
                    hover:bg-white/20
                    focus:outline-none focus:ring-4 focus:ring-white/20
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-all duration-200
                    flex items-center justify-center gap-2
                  `}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                  <span>
                    {biometricType === 'faceId' ? 'Sign in with Face ID' : 
                     biometricType === 'touchId' ? 'Sign in with Touch ID' : 
                     'Sign in with Biometrics'}
                  </span>
                </button>
              </>
            )}

            {/* Footer Info */}
            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-white/70">
                MIBOT Onboarding Platform
              </p>
              <p className="text-xs text-blue-300/60 mt-2">
                Secure • Professional • Efficient
              </p>
            </div>
          </div>

          {/* Bottom Branding */}
          <div className="mt-6 text-center">
            <p className="text-white/40 text-xs">
              © 2025 MIBOT. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Subtle Animated Gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent pointer-events-none"></div>
    </div>
  );
};

export default Login;
